import { createContext, FC, useCallback, useEffect, useState } from 'react';

import config from '@shared/config';

import { ContextProps } from './SettingsContext.types';

const localStorageKey = `${import.meta.env.VITE__CVT_PROJECT_KEY}-theme-mode`;
const navigationStateStorageKey = `${import.meta.env.VITE__CVT_PROJECT_KEY}-navigation-state`;

const defaultContext: ContextProps = {
  drawerWidth: config.theme.drawerWidth,
  mode: config.theme.defaultMode,
  setMode: () => null,
  toggleMode: () => null,
  sidebar: 'open',
  setSidebar: () => null,
  toggleSidebar: () => null,
};

const getModeFromLocalStorage = (): CVT.Theme.Settings['mode'] => {
  return localStorage.getItem(localStorageKey) as CVT.Theme.Settings['mode'] || 'light';
};

const getSidebarStateFromLocalStorage = (): CVT.Settings['sidebar'] => {
  return localStorage.getItem(navigationStateStorageKey) as CVT.Settings['sidebar'] || 'open';
};

export const SettingsContext = createContext(defaultContext);

export const SettingsProvider: FC<React.PropsWithChildren> = ({ children }) => {

  const [mode, setMode] = useState<CVT.Theme.Settings['mode']>(getModeFromLocalStorage());
  const [sidebar, setSidebar] = useState<CVT.Settings['sidebar']>(getSidebarStateFromLocalStorage());

  useEffect(() => {
    localStorage.setItem(localStorageKey, mode);
  },[mode]);

  const toggleMode = useCallback(() => {
    setMode(mode => mode === 'light' ? 'dark' : 'light');
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebar(sidebar => sidebar === 'open' ? 'closed' : 'open');
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        drawerWidth: config.theme.drawerWidth,
        mode,
        setMode,
        toggleMode,
        sidebar,
        setSidebar,
        toggleSidebar,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;
